import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MapPin, Clock, User, Phone, ShoppingBag, CreditCard, StickyNote } from "lucide-react";
import { amountCents, formatCents } from "@/lib/kpis/demoKpis";

interface DemoOrderItem {
  item_id?: string;
  item_name?: string;
  quantity?: number;
  price?: number;
  notes?: string;
}

interface DemoOrderShape {
  id?: string;
  order_code?: string;
  status?: string;
  table_number?: string;
  customer_name?: string;
  customer_phone?: string;
  items?: DemoOrderItem[];
  total_amount?: number;
  payment_method?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

interface DemoOrderDetailsDrawerProps {
  order: DemoOrderShape | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const statusColorMap: Record<string, string> = {
  new: "bg-warning text-warning-foreground",
  preparing: "bg-primary text-primary-foreground",
  ready: "bg-success text-success-foreground",
  served: "bg-muted text-muted-foreground",
  cancelled: "bg-destructive text-destructive-foreground",
};


const getStatusBadgeClass = (status?: string) => {
  const key = status?.toLowerCase() || "";
  return statusColorMap[key] || "bg-muted text-muted-foreground";
};

export const DemoOrderDetailsDrawer = ({ order, open, onOpenChange }: DemoOrderDetailsDrawerProps) => {
  if (!order) return null;

  const placedAt = order.created_at || order.updated_at;
  const items = Array.isArray(order.items) ? order.items : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-hidden">
        <SheetHeader className="space-y-4">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-xl font-semibold flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-primary" />
              Order Details
            </SheetTitle>
            <Badge className={getStatusBadgeClass(order.status)}>
              {order.status || "Unknown"}
            </Badge>
          </div>

          <SheetDescription>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {order.table_number ? ("Table " + order.table_number) : "Counter"}
              </div>
              {placedAt && (
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {new Date(placedAt).toLocaleString()}
                </div>
              )}
              {order.customer_name && (
                <div className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {order.customer_name}
                </div>
              )}
              {order.customer_phone && (
                <div className="flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  {order.customer_phone}
                </div>
              )}
            </div>
          </SheetDescription>

          <div className="flex items-center gap-2">
            <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
              {order.order_code || order.id || "–"}
            </span>
          </div>
        </SheetHeader>

        <Separator className="my-4" />

        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Items
              </h3>
              <div className="space-y-3">
                {items.map((item, index) => {
                  const keyPart = item.item_id ?? String(index);
                  const itemKey = keyPart + "-" + String(index);
                  const quantity = item.quantity ?? 0;

                  return (
                    <div key={itemKey} className="rounded-lg border bg-card p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">
                            {quantity}x {item.item_name || "Item"}
                          </div>
                          {item.notes && (
                            <div className="text-xs text-muted-foreground mt-1">
                              <span className="flex items-center gap-1">
                                <StickyNote className="h-3 w-3" />
                                {item.notes}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="text-sm font-semibold">
                          {formatCents((item.price_snapshot_cents ?? item.price_cents ?? (item.price ?? 0) * 100) * (item.quantity ?? 1))}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {items.length === 0 && (
                  <div className="rounded-lg border border-dashed bg-muted/50 p-6 text-center text-sm text-muted-foreground">
                    No items found for this order.
                  </div>
                )}
              </div>
            </div>

            <Separator />

            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Payment Method</span>
                <span className="flex items-center gap-1 font-medium">
                  <CreditCard className="h-4 w-4" />
                  {order.payment_method || "N/A"}
                </span>
              </div>

              <div className="flex items-center justify-between text-base font-semibold">
                <span>Total</span>
                <span>{formatCents(amountCents(order))}</span>
              </div>
            </div>

            {order.notes && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Notes
                </h3>
                <div className="rounded-lg border bg-muted/30 p-3 text-sm">
                  {order.notes}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default DemoOrderDetailsDrawer;
